import { format } from "date-fns";
import { MapPin, Truck, Calendar, FileText, User } from "lucide-react";
import type { MyTruckRow } from "@/hooks/useMyTrucks";

interface Props {
  truck: MyTruckRow;
}

function Section({ icon: Icon, title, children }: { icon: React.ElementType; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{title}</span>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex justify-between text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value ?? '—'}</span>
    </div>
  );
}

export function TruckDetailsPanel({ truck }: Props) {
  const contactParts = truck.contactInfo ? truck.contactInfo.split(' | ') : [];

  return (
    <div className="border-l-2 border-l-accent bg-muted/20 px-4 py-3">
      <div className="grid grid-cols-3 gap-3">
        {/* Column 1: Trip & Equipment */}
        <div className="space-y-3">
          <Section icon={MapPin} title="Route">
            <Field label="Origin" value={truck.origin} />
            <Field label="Destination" value={truck.destination === 'Anywhere' ? '🌐 Anywhere' : truck.destination} />
          </Section>
          <Section icon={Truck} title="Equipment">
            <Field label="Type" value={truck.equipmentType} />
            <Field label="Length" value={truck.lengthFt > 0 ? `${truck.lengthFt} ft` : '—'} />
            <Field label="Max Weight" value={truck.weightLbs > 0 ? `${truck.weightLbs.toLocaleString()} lbs` : '—'} />
            <Field label="Load Type" value={truck.loadType} />
          </Section>
        </div>

        {/* Column 2: Availability & Notes */}
        <div className="space-y-3">
          <Section icon={Calendar} title="Availability">
            <Field label="Available From" value={format(new Date(truck.availableFrom), 'MMM dd, yyyy')} />
            <Field label="Available To" value={format(new Date(truck.availableTo), 'MMM dd, yyyy')} />
            {truck.ratePerMile != null && (
              <Field label="Rate/Mile" value={`$${truck.ratePerMile}`} />
            )}
          </Section>
          {truck.comments && (
            <Section icon={FileText} title="Notes">
              <p className="text-[11px] text-foreground whitespace-pre-wrap">{truck.comments}</p>
            </Section>
          )}
        </div>

        {/* Column 3: Contact & Meta */}
        <div className="space-y-3">
          {contactParts.length > 0 && (
            <Section icon={User} title="Contact">
              {contactParts.map((part, i) => (
                <p key={i} className="text-[11px] text-foreground">{part}</p>
              ))}
            </Section>
          )}
          <Section icon={FileText} title="Details">
            <Field label="Reference" value={truck.ref} />
            <Field label="Posted" value={format(new Date(truck.createdAt), 'MMM dd, yyyy h:mm a')} />
            <Field label="Status" value={truck.status.charAt(0).toUpperCase() + truck.status.slice(1)} />
          </Section>
        </div>
      </div>
    </div>
  );
}
